import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Bug, Leaf, Landmark, ShieldAlert, ExternalLink, Phone, ArrowRight, Camera, CheckCircle2 } from 'lucide-react';

export const GOV_REPORT = 'https://www.gov.uk/report-flytipping';
const MET = 'https://www.met.police.uk/advice/advice-and-information/asb/asb/antisocial-behaviour/fly-tipping-and-other-littering/';
const REGISTER = 'https://environment.data.gov.uk/public-register/waste-carriers-brokers/registration/CBDU666099';
const REGISTER_SEARCH = 'https://environment.data.gov.uk/public-register/view/index';

const PHOTOS = [
  { src: 'https://images.unsplash.com/photo-1759401654832-ab2e73a14336?auto=format&fit=crop&w=900&q=70', alt: 'Black rubbish bags dumped in a pile on a pavement next to parked cars', w: 900, h: 600 },
  { src: 'https://images.unsplash.com/photo-1620250404922-bffa1823fce3?auto=format&fit=crop&w=900&q=70', alt: 'Bags of waste and cardboard left along the front of a row of terraced houses', w: 900, h: 600 },
  { src: 'https://images.pexels.com/photos/29348622/pexels-photo-29348622.jpeg?auto=compress&cs=tinysrgb&w=900', alt: 'A discarded mattress and mixed waste dumped on open ground', w: 900, h: 600 },
];

const IMPACTS = [
  { icon: AlertTriangle, title: 'Unsafe streets', text: 'Dumped waste can block pavements and roads and may hide sharp or dangerous items.' },
  { icon: Bug, title: 'Pests', text: 'Rubbish left out attracts rats, foxes and flies.' },
  { icon: Leaf, title: 'Wildlife & environment', text: 'Waste can pollute land and water and harm animals that get trapped in it or eat it.' },
  { icon: Landmark, title: 'Clean-up burden', text: 'Councils and landowners have to pay to clear it — money that comes from local budgets.' },
];

const Card = ({ icon: Icon, title, text }) => (
  <div className="rounded-xl bg-white border border-slate-200 p-5">
    <Icon className="w-6 h-6 text-[color:var(--mv-orange)]" aria-hidden="true" />
    <div className="mt-3 font-bold text-[color:var(--mv-navy)]">{title}</div>
    <p className="mt-1 text-sm text-slate-600">{text}</p>
  </div>
);

const ReportCta = ({ testid }) => (
  <a href={GOV_REPORT} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid={testid}>
    Report fly-tipping on GOV.UK <ExternalLink className="w-4 h-4" aria-hidden="true" />
  </a>
);

export const FlyTippingTeaser = () => (
  <section className="section bg-white" data-testid="flytipping-teaser">
    <div className="container-xl grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
      <figure>
        <img src={PHOTOS[0].src} alt={PHOTOS[0].alt} width={PHOTOS[0].w} height={PHOTOS[0].h} loading="lazy" className="rounded-xl w-full aspect-[3/2] object-cover" />
        <figcaption className="mt-2 text-xs text-slate-500">Illustrative stock photo — not a Move Van job.</figcaption>
      </figure>
      <div>
        <div className="text-[color:var(--mv-orange)] font-bold tracking-[0.2em] text-xs uppercase mb-3">Community</div>
        <h2 className="font-display text-3xl md:text-4xl font-extrabold text-[color:var(--mv-navy)]">Help keep our streets clear of fly-tipping</h2>
        <p className="text-slate-600 mt-4">Fly-tipping makes streets unsafe, attracts pests, harms wildlife and costs councils money to clean up. If you see it, you can report it to your local council through the official GOV.UK service.</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <ReportCta testid="flytipping-teaser-report" />
          <Link to="/fly-tipping" className="btn-outline-dark" data-testid="flytipping-teaser-more">How to report safely <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <p className="mt-3 text-xs text-slate-500">Reporting opens the official GOV.UK page and is free. It's separate from booking a paid clearance with us.</p>
      </div>
    </div>
  </section>
);

export default function FlyTipping() {
  return (
    <main data-testid="flytipping-page">
      <section className="bg-[color:var(--mv-navy)] text-white">
        <div className="container-xl py-14 md:py-20 max-w-4xl">
          <div className="text-[color:var(--mv-orange-2)] font-bold tracking-[0.2em] text-xs uppercase mb-3">Community guide</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold">Seen fly-tipping? Here's how to report it</h1>
          <p className="mt-4 text-white/85 text-lg max-w-2xl">Fly-tipping is the illegal dumping of waste. Reporting it helps your council clear it and track down who left it.</p>
          <div className="mt-7"><ReportCta testid="flytipping-report-hero" /></div>
          <p className="mt-3 text-sm text-white/70">This opens the official GOV.UK reporting service, which passes your report to the right local council. It is free and separate from Move Van's paid clearance service.</p>
        </div>
      </section>

      <section className="section">
        <div className="container-xl">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[color:var(--mv-navy)]">Why it matters</h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{IMPACTS.map(i => <Card key={i.title} {...i} />)}</div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {PHOTOS.map(p => (
              <figure key={p.src}>
                <img src={p.src} alt={p.alt} width={p.w} height={p.h} loading="lazy" className="rounded-lg w-full aspect-[3/2] object-cover" />
                <figcaption className="mt-1.5 text-xs text-slate-500">Illustrative stock photo — not a Move Van job.</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-[color:var(--mv-sky)]">
        <div className="container-xl grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="rounded-xl bg-white border border-slate-200 p-6" data-testid="flytipping-safety">
            <ShieldAlert className="w-6 h-6 text-red-600" aria-hidden="true" />
            <h2 className="mt-3 font-display text-2xl font-extrabold text-[color:var(--mv-navy)]">Stay safe</h2>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[color:var(--mv-orange)] shrink-0" />Don't touch or move dumped waste — it may contain needles, broken glass, chemicals or asbestos.</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[color:var(--mv-orange)] shrink-0" />Don't confront anyone you see dumping waste.</li>
              <li className="flex gap-2"><Camera className="w-4 h-4 mt-0.5 text-[color:var(--mv-orange)] shrink-0" />Only if it's safe: note the location, date and time, what the waste is and roughly how much, and any vehicle details. Take photos if you can do so safely.</li>
            </ul>
          </div>
          <div className="rounded-xl bg-white border border-slate-200 p-6" data-testid="flytipping-where">
            <h2 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)]">Who to tell</h2>
            <div className="mt-3 text-sm text-slate-700 space-y-3">
              <p><strong>Most fly-tipping</strong> (bags, furniture, a van load) — report it to your <strong>local council</strong> through GOV.UK.</p>
              <ReportCta testid="flytipping-report-council" />
              <p className="pt-2"><strong>Large-scale or organised dumping, waste crime, or waste that's an immediate danger</strong> to people or the environment (for example leaking chemicals or drums, or waste in a river) — call the <strong>Environment Agency incident hotline</strong>, open 24 hours:</p>
              <a href="tel:08008070060" className="btn-outline-dark" data-testid="flytipping-ea-hotline"><Phone className="w-4 h-4" /> 0800 80 70 60</a>
              <p className="text-xs text-slate-500">If someone is in danger or a crime is happening now, call 999.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div data-testid="flytipping-responsible">
            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[color:var(--mv-navy)]">Getting rid of your own waste? Check who takes it</h2>
            <p className="mt-3 text-slate-600 text-sm">If you pay someone to take your rubbish and it ends up fly-tipped, you can be fined too. Before you hand waste over, check the person or company is a registered waste carrier and ask where it's going.</p>
            <a href={REGISTER_SEARCH} target="_blank" rel="noopener noreferrer" className="mt-4 btn-outline-dark" data-testid="flytipping-check-register">Check a waste carrier on the public register <ExternalLink className="w-4 h-4" /></a>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-6" data-testid="flytipping-movevan">
            <div className="font-bold text-[color:var(--mv-navy)]">Booking a clearance with Move Van</div>
            <p className="mt-2 text-sm text-slate-600">Move Van Ltd is listed on the Environment Agency public register of waste carriers, brokers and dealers as <strong>MOVEVAN LTD</strong>, registration <strong>CBDU666099</strong> (upper tier). Being registered is a legal requirement — it isn't an endorsement by the Environment Agency.</p>
            <p className="mt-2 text-xs text-slate-500">Hazardous waste such as asbestos or chemicals needs specialist handling — tell us what you have before booking and we'll let you know if we can help.</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a href={REGISTER} target="_blank" rel="noopener noreferrer" className="btn-outline-dark" data-testid="flytipping-our-registration">See our register entry <ExternalLink className="w-4 h-4" /></a>
              <Link to="/#quote" className="btn-primary" data-testid="flytipping-quote">Get a clearance quote <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="container-xl">
          <div className="text-xs text-slate-500 border-t border-slate-200 pt-4" data-testid="flytipping-sources">
            <strong>Further information:</strong>{' '}
            <a className="underline" href={GOV_REPORT} target="_blank" rel="noopener noreferrer">GOV.UK — Report fly-tipping</a> ·{' '}
            <a className="underline" href={MET} target="_blank" rel="noopener noreferrer">Metropolitan Police — Fly-tipping and other littering</a> ·{' '}
            <a className="underline" href={REGISTER_SEARCH} target="_blank" rel="noopener noreferrer">Environment Agency public register</a>.
            {' '}Photos: Unsplash and Pexels (free licence), illustrative only.
          </div>
        </div>
      </section>
    </main>
  );
}
